// Hand-drawn tour routes — the pure half, shared by the Tour Creator's Route
// Adjuster (which draws them) and the Eng Tour viewer (which walks them).
//
// A tour is walked as LEGS: the start point → stop 1, then each stop → the
// next. Each leg is routed automatically by /api/pathfind unless the editor
// has drawn it by hand, in which case the tour follows the drawn node list.
// Drawn routes live on the TOUR (`tour.routes`), never on a stop: a stop's
// fields are copied into the stop bank, into other tours and into the public
// Free Roam list, and a path between two particular places is none of those.
//
// A route is keyed by the identity of its two ends and the routing mode:
//   `${fromKey}|${toKey}|${mode}` → { nodeIds, ts, by }
// `fromKey` is "start" for the first leg, else the previous stop's key. Keying
// by the ends rather than by position means reordering, inserting or
// reassigning a stop makes exactly the affected routes stop matching — they
// are pruned on save — and everything else keeps its drawing.
//
// Two modes, drawn separately: "std" (the ordinary tour, stairs allowed) and
// "a11y" (accessible — no stairs, no non-accessible nodes). A drawing made for
// one is never used for the other.
//
// Pinned by src/engtour.routes.test.js.

export const START_KEY = "start";
export const ROUTE_MODES = {
  std:  { label: "Normal",     color: "#0093D0" },
  a11y: { label: "Accessible", color: "#2E7D32" },
};
export const MODE_IDS = Object.keys(ROUTE_MODES);

// The start of every tour. Mirrors what the viewer has always done: the known
// atrium node if it exists, else a node called "Atrium", else the node nearest
// the EITC centre — so a tour always has a start rather than no first leg.
export const EITC_CENTER = { lat: 49.8085709, lng: -97.1340870 };
export const START_NODE_ID = "id_1773888584940_803";
export const START_LABEL = "Atrium";

export function resolveStartNode(nodes) {
  const list = nodes || [];
  const known = list.find(n => n.id === START_NODE_ID);
  if (known) return { id: known.id, floor: Number.isFinite(known.floor) ? known.floor : 2 };
  const named = list.find(n => String(n.room_number ?? n.label ?? "").trim().toLowerCase() === "atrium");
  if (named) return { id: named.id, floor: Number.isFinite(named.floor) ? named.floor : 2 };
  let best = null, bestD = Infinity;
  for (const n of list) {
    if (!Number.isFinite(n.lat) || !Number.isFinite(n.lng)) continue;
    const d = (n.lat - EITC_CENTER.lat) ** 2 + (n.lng - EITC_CENTER.lng) ** 2;
    if (d < bestD) { bestD = d; best = n; }
  }
  return best ? { id: best.id, floor: Number.isFinite(best.floor) ? best.floor : 2 } : { id: START_NODE_ID, floor: 2 };
}

export function haversineMeters(a, b) {
  const R = 6371000;
  const p1 = a.lat * Math.PI / 180, p2 = b.lat * Math.PI / 180;
  const dp = (b.lat - a.lat) * Math.PI / 180;
  const dl = (b.lng - a.lng) * Math.PI / 180;
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// ── Identity ─────────────────────────────────────────────────────────────────
const normBuilding = (b) => (b == null ? "" : String(b).toLowerCase().replace(/^eitc[\s_-]*/, "").trim());

/** A stop's identity: its pinned nav node, else building + room. Same rule as the stop bank's key. */
export function stopKey(s) {
  if (!s) return "";
  if (s.nodeId) return `node::${s.nodeId}`;
  return `${normBuilding(s.building)}::${String(s.room == null ? "" : s.room).toLowerCase()}`;
}

export function routeKey(fromKey, toKey, mode) {
  return `${fromKey}|${toKey}|${mode}`;
}

/**
 * The legs of a tour in walking order. A tour starts AT its first stop ("Start
 * Here!"), so there is no leg to stop 0: leg `idx` (idx >= 1) walks stop idx-1
 * to stop idx, and a one-stop tour has no legs. (Until 2026-10 a leg 0 ran from
 * a fixed start point in the Atrium to stop 0, keyed `start|…`; `pruneRoutes`
 * drops any such drawing still on a tour, since nothing walks it now.)
 */
export function legsOf(tour) {
  const stops = Array.isArray(tour?.stops) ? tour.stops : [];
  return stops.slice(1).map((s, k) => ({
    idx: k + 1,
    fromKey: stopKey(stops[k]),
    toKey: stopKey(s),
    fromStop: stops[k],
    toStop: s,
  }));
}

/** The leg arriving at stop `idx`, or undefined (there is none for stop 0). */
export function legAt(tour, idx) {
  return legsOf(tour).find(l => l.idx === idx);
}

/** The stored drawing for leg `idx` in `mode`, or null. */
export function storedRoute(tour, idx, mode) {
  const leg = legAt(tour, idx);
  if (!leg) return null;
  const entry = tour?.routes?.[routeKey(leg.fromKey, leg.toKey, mode)];
  return entry && Array.isArray(entry.nodeIds) && entry.nodeIds.length ? entry : null;
}

/** A copy of the tour with leg `idx`'s drawing in `mode` set (or removed, when `entry` is null). */
export function withRoute(tour, idx, mode, entry) {
  const leg = legAt(tour, idx);
  if (!leg) return tour;
  const key = routeKey(leg.fromKey, leg.toKey, mode);
  const routes = { ...(tour.routes || {}) };
  if (entry) routes[key] = { nodeIds: [...entry.nodeIds], ts: entry.ts ?? Date.now(), ...(entry.by ? { by: entry.by } : {}) };
  else delete routes[key];
  const next = { ...tour };
  if (Object.keys(routes).length) next.routes = routes; else delete next.routes;
  return next;
}

/**
 * Drop drawings for pairs that are no longer consecutive stops of this tour.
 * Returns the SAME object when nothing changes, so callers can skip a re-render.
 */
export function pruneRoutes(tour) {
  if (!tour?.routes) return tour;
  const live = new Set();
  for (const leg of legsOf(tour)) for (const m of MODE_IDS) live.add(routeKey(leg.fromKey, leg.toKey, m));
  const keep = Object.fromEntries(Object.entries(tour.routes).filter(([k]) => live.has(k)));
  if (Object.keys(keep).length === Object.keys(tour.routes).length) return tour;
  const next = { ...tour };
  if (Object.keys(keep).length) next.routes = keep; else delete next.routes;
  return next;
}

/**
 * Can the viewer walk this drawing today? Every node must still exist, no node
 * may repeat back-to-back, and it must end at one of the destination's doors.
 * Stale data (a node renumbered upstream) is the normal way this fails, and the
 * answer is to route automatically, not to draw a broken line.
 */
export function validateRoute(entry, nodeById, destNodeIds) {
  const ids = entry?.nodeIds;
  if (!Array.isArray(ids) || ids.length === 0) return { ok: false, reason: "empty" };
  const get = typeof nodeById?.get === "function" ? (id) => nodeById.get(id) : (id) => nodeById?.[id];
  for (let i = 0; i < ids.length; i++) {
    if (!get(ids[i])) return { ok: false, reason: `missing node ${ids[i]}` };
    if (i > 0 && ids[i] === ids[i - 1]) return { ok: false, reason: "repeated node" };
  }
  if (destNodeIds && destNodeIds.length && !destNodeIds.includes(ids[ids.length - 1])) {
    return { ok: false, reason: "does not end at the destination" };
  }
  return { ok: true };
}

// ── Walking the graph (for the Route Adjuster's hover path and shafts) ───────

/** Undirected adjacency: id → [{ to, edge }]. */
export function buildAdjacency(edges) {
  const adj = new Map();
  const add = (a, b, edge) => { if (!adj.has(a)) adj.set(a, []); adj.get(a).push({ to: b, edge }); };
  for (const e of edges || []) {
    if (!e || !e.from || !e.to || e.from === e.to) continue;
    add(e.from, e.to, e); add(e.to, e.from, e);
  }
  return adj;
}

const isStair = (n) => n?.type === "stair";
const isBlocked = (n) => n?.type === "non-accessible";

/** May a route in `mode` use the edge between nodes a and b? */
export function edgeAllowed(mode, a, b) {
  if (mode === "a11y") return !(isStair(a) || isStair(b) || isBlocked(a) || isBlocked(b));
  return true;
}

const isElevator = (n) => n?.type === "elevator";

/**
 * Shortest path along edges from `fromId` to `toId`, as node ids (inclusive),
 * or null. `opts` is a mode string or `{ mode, costModel, allowNode }`:
 *
 *  - costModel "editor" (default): what the Route Adjuster uses while the
 *    editor hovers. Metres × edge weight and nothing else — an editor hovering
 *    an elevator means the elevator. In "a11y" stairs and non-accessible nodes
 *    are off-limits (`edgeAllowed`).
 *  - costModel "server": a mirror of /api/pathfind's Dijkstra, so the adjuster
 *    can show the AUTOMATIC route the live tour will walk without a request per
 *    leg. "std" sends avoidElevators, which multiplies any edge touching an
 *    elevator by 1000; "a11y" drops every edge touching a stair and multiplies
 *    elevator edges by 2. The server does NOT refuse non-accessible nodes, so
 *    neither does this model — the point is to draw what the viewer draws.
 *  - allowNode(node): an optional filter on which nodes may be entered (the
 *    adjuster keeps a hover path on the floor being looked at, so a click on a
 *    far node never silently rides a staircase).
 */
export function shortestPath(nodeById, adj, fromId, toId, opts = "std") {
  const o = typeof opts === "string" ? { mode: opts } : (opts || {});
  const mode = o.mode || "std";
  const server = o.costModel === "server";
  const allowNode = typeof o.allowNode === "function" ? o.allowNode : null;
  const allowed = (a, b) => (server ? !(mode === "a11y" && (isStair(a) || isStair(b))) : edgeAllowed(mode, a, b));
  const factor = (a, b) => (server && (isElevator(a) || isElevator(b)) ? (mode === "a11y" ? 2 : 1000) : 1);
  const get = typeof nodeById?.get === "function" ? (id) => nodeById.get(id) : (id) => nodeById?.[id];
  if (!get(fromId) || !get(toId)) return null;
  if (fromId === toId) return [fromId];
  const dist = new Map([[fromId, 0]]);
  const prev = new Map();
  const done = new Set();
  // A plain array scan is fine at this size (a few thousand nodes, one hover).
  const open = [fromId];
  while (open.length) {
    let bi = 0;
    for (let i = 1; i < open.length; i++) if (dist.get(open[i]) < dist.get(open[bi])) bi = i;
    const cur = open.splice(bi, 1)[0];
    if (cur === toId) break;
    if (done.has(cur)) continue;
    done.add(cur);
    const a = get(cur);
    for (const { to, edge } of adj.get(cur) || []) {
      const b = get(to);
      if (!b || done.has(to) || !allowed(a, b)) continue;
      if (allowNode && to !== toId && !allowNode(b)) continue;
      const w = (haversineMeters(a, b) * (Number(edge?.weight) || 1) || 0.01) * factor(a, b);
      const nd = dist.get(cur) + w;
      if (nd < (dist.get(to) ?? Infinity)) { dist.set(to, nd); prev.set(to, cur); if (!open.includes(to)) open.push(to); }
    }
  }
  if (!prev.has(toId)) return null;
  const path = [toId];
  while (path[path.length - 1] !== fromId) path.push(prev.get(path[path.length - 1]));
  return path.reverse();
}

/**
 * The floors a stair or elevator reaches from `nodeId`: the shaft is the chain
 * of same-type nodes joined floor to floor (one floor per edge), so riding to
 * floor 4 from floor 1 passes 2 and 3 — each entry's `path` has every node
 * from `nodeId` (exclusive) to that floor's shaft node (inclusive).
 * Returns [{ floor, nodeId, path }] for every OTHER floor, nearest first,
 * or [] when the node is not a shaft.
 */
export function shaftFloors(nodeId, nodeById, adj) {
  const get = typeof nodeById?.get === "function" ? (id) => nodeById.get(id) : (id) => nodeById?.[id];
  const start = get(nodeId);
  if (!start || (start.type !== "stair" && start.type !== "elevator")) return [];
  const out = [];
  const seen = new Set([nodeId]);
  const queue = [{ id: nodeId, path: [] }];
  while (queue.length) {
    const { id, path } = queue.shift();
    const here = get(id);
    for (const { to } of adj.get(id) || []) {
      if (seen.has(to)) continue;
      const n = get(to);
      if (!n || n.type !== start.type || n.floor === here.floor) continue; // only the shaft's own floor links
      seen.add(to);
      const p = [...path, to];
      out.push({ floor: n.floor, nodeId: to, path: p });
      queue.push({ id: to, path: p });
    }
  }
  out.sort((a, b) => Math.abs(a.floor - start.floor) - Math.abs(b.floor - start.floor) || a.floor - b.floor);
  return out;
}

/**
 * The automatic route between two places, each given as its door node ids:
 * the cheapest server-model path over every (start door, end door) pair, or
 * null. This is what the live tour's leg builder gets from /api/pathfind.
 * Returns { ids, cost }.
 */
export function bestAutoPath(nodeById, adj, fromIds, toIds, mode = "std") {
  let best = null;
  for (const a of fromIds || []) {
    for (const b of toIds || []) {
      const ids = shortestPath(nodeById, adj, a, b, { mode, costModel: "server" });
      if (!ids) continue;
      const get = typeof nodeById?.get === "function" ? (id) => nodeById.get(id) : (id) => nodeById?.[id];
      let cost = 0;
      for (let i = 1; i < ids.length; i++) cost += haversineMeters(get(ids[i - 1]), get(ids[i]));
      if (!best || cost < best.cost) best = { ids, cost };
    }
  }
  return best;
}

// ── Drawing a leg floor by floor ─────────────────────────────────────────────
/** A floor that is not a storey: outdoors, or unknown. */
export const isOutdoorFloor = (f) => f === 0 || f === "outdoor" || f == null;

/**
 * Split a walked node list into one polyline per floor, plus the floor changes
 * to draw a tap-to-switch box at. A STAIR OR ELEVATOR RUN IS ONE CHANGE: the
 * graph carries a shaft node per floor, so a climb from 100 to 300 arrives as
 * 1 → 2 → 3 and used to need two taps, the middle one landing on a floor nobody
 * walks on. Consecutive shaft nodes collapse to the last one — the floor you
 * step out on. Any non-shaft node breaks the run, so stairs → corridor → more
 * stairs on an intermediate floor still gets its own box. Each floor's line
 * begins at the point the previous one ended (the "bridge"), so the two meet.
 * Outdoor↔indoor changes split the line but get no box.
 * Returns { segments: [{ floor, coords: [{lat,lng}] }], transitions: [{ lat,
 * lng, fromFloor, toFloor, type: "stair"|"elevator" }] }.
 */
export function splitLegByFloor(nodes) {
  const segments = [], transitions = [];
  if (!Array.isArray(nodes) || !nodes.length) return { segments, transitions };
  const isShaft = (n) => n?.type === "stair" || n?.type === "elevator";
  let segFloor = nodes[0]?.floor ?? 2, segCoords = [];
  for (let i = 0; i < nodes.length; i++) {
    let node = nodes[i];
    let nodeFloor = node?.floor ?? 2;
    const prev = nodes[i - 1];
    if (nodeFloor !== segFloor && isShaft(prev) && isShaft(node)) {
      let j = i;
      while (j + 1 < nodes.length && isShaft(nodes[j + 1])) j++;
      i = j; node = nodes[j]; nodeFloor = node?.floor ?? 2;
      if (nodeFloor === segFloor) { segCoords.push({ lat: node.lat, lng: node.lng }); continue; }
    }
    if (nodeFloor !== segFloor) {
      const bridge = segCoords[segCoords.length - 1];
      segments.push({ floor: segFloor, coords: segCoords });
      if (bridge && !isOutdoorFloor(segFloor) && !isOutdoorFloor(nodeFloor)) {
        transitions.push({ lat: bridge.lat, lng: bridge.lng, fromFloor: segFloor, toFloor: nodeFloor, type: prev?.type === "elevator" ? "elevator" : "stair" });
      }
      segFloor = nodeFloor; segCoords = [bridge];
    }
    segCoords.push({ lat: node.lat, lng: node.lng });
  }
  segments.push({ floor: segFloor, coords: segCoords });
  return { segments, transitions };
}
